import React, { useContext, useEffect, useState } from 'react';
import { Autocomplete, Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { MobileDateTimePicker } from '@mui/x-date-pickers';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import { campaignsPathTo, tagsPathTo } from '../utils/Api';
import SnackbarContext from '../contexts/SnackbarContext';
import FormBox from './building_blocks/FormBox';
import PageHeader from './building_blocks/PageHeader';

const discountTypeOptions = [
  { value: 'none', label: 'Sem desconto' },
  { value: 'percent', label: 'Percentual (%)' },
  { value: 'fixed_cents', label: 'Valor fixo (centavos)' },
  { value: 'waive', label: 'Isentar' },
];

const blankCampaign = {
  name: '',
  slug: '',
  starts_at: null,
  ends_at: null,
  enrollment_opens_at: null,
  enrollment_ends_at: null,
  platform_fee_discount_type: 'none',
  platform_fee_discount_value: 0,
  delivery_fee_discount_type: 'none',
  delivery_fee_discount_value: 0,
  required_tags: [],
};

const toMomentDateTime = (value) => {
  const date = moment(value);
  return date.isValid() ? date : null;
};

const toApiDateTime = (value) => (value && value.isValid() ? value.toISOString() : null);

const CampaignForm = () => {
  const { id } = useParams();
  const isEditing = !!id;
  const navigate = useNavigate();
  const { openSnackbar } = useContext(SnackbarContext);
  const [campaign, setCampaign] = useState(blankCampaign);
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.title = isEditing ? 'Editar campanha - TudoPets.app' : 'Nova campanha - TudoPets.app';
  }, [isEditing]);

  useEffect(() => {
    const requests = [tagsPathTo.list()];
    if (isEditing) {
      requests.push(campaignsPathTo.get(id));
    }

    Promise.all(requests)
      .then(([tagsResult, campaignResult]) => {
        setTags(tagsResult.data);

        if (campaignResult) {
          const loadedCampaign = campaignResult.data;
          setCampaign({
            ...loadedCampaign,
            starts_at: toMomentDateTime(loadedCampaign.starts_at),
            ends_at: toMomentDateTime(loadedCampaign.ends_at),
            enrollment_opens_at: toMomentDateTime(loadedCampaign.enrollment_opens_at),
            enrollment_ends_at: toMomentDateTime(loadedCampaign.enrollment_ends_at),
          });
          setSelectedTags(loadedCampaign.required_tags || []);
        }
      })
      .finally(() => setLoading(false));
  }, [id, isEditing]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setCampaign({ ...campaign, [name]: value });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);

    try {
      const payload = {
        name: campaign.name,
        slug: campaign.slug,
        starts_at: toApiDateTime(campaign.starts_at),
        ends_at: toApiDateTime(campaign.ends_at),
        enrollment_opens_at: toApiDateTime(campaign.enrollment_opens_at),
        enrollment_ends_at: toApiDateTime(campaign.enrollment_ends_at),
        platform_fee_discount_type: campaign.platform_fee_discount_type,
        platform_fee_discount_value: Number(campaign.platform_fee_discount_value || 0),
        delivery_fee_discount_type: campaign.delivery_fee_discount_type,
        delivery_fee_discount_value: Number(campaign.delivery_fee_discount_value || 0),
        required_tag_ids: selectedTags.map((tag) => tag.id),
      };

      if (isEditing) {
        await campaignsPathTo.update(id, payload);
      } else {
        await campaignsPathTo.save(payload);
      }

      openSnackbar('Campanha salva com sucesso!', 'success');
      navigate('/control-center/campaigns');
    } catch (error) {
      openSnackbar(error?.response?.data?.errors?.[0] || 'Não foi possível salvar a campanha.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const renderDateTimePicker = (label, field) => (
    <MobileDateTimePicker
      label={label}
      value={campaign[field]}
      onChange={(value) => setCampaign({ ...campaign, [field]: value })}
      format="DD/MM/YYYY HH:mm"
      ampm={false}
      closeOnSelect={false}
      sx={{ width: '100%', bgcolor: '#F5F8FA', borderRadius: '24px' }}
      slotProps={{
        actionBar: {
          actions: ['cancel', 'accept'],
        },
      }}
    />
  );

  if (loading) {
    return <Typography>Carregando campanha...</Typography>;
  }

  return (
    <FormBox>
      <Box display="flex" flexDirection="column" gap={2}>
        <PageHeader
          title={isEditing ? 'Editar campanha' : 'Nova campanha'}
          onBack={() => navigate('/control-center/campaigns')}
        />

        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              name="name"
              label="Nome da campanha"
              value={campaign.name}
              onChange={handleChange}
              fullWidth
              required
            />

            <TextField
              name="slug"
              label="Slug"
              value={campaign.slug || ''}
              onChange={handleChange}
              helperText="Opcional. Se vazio, será gerado pelo nome."
              fullWidth
            />

            {renderDateTimePicker('Início da campanha', 'starts_at')}
            {renderDateTimePicker('Fim da campanha', 'ends_at')}
            {renderDateTimePicker('Início das inscrições', 'enrollment_opens_at')}
            {renderDateTimePicker('Fim das inscrições', 'enrollment_ends_at')}

            <Box display="flex" gap={2}>
              <TextField
                select
                name="delivery_fee_discount_type"
                label="Desconto entrega"
                value={campaign.delivery_fee_discount_type}
                onChange={handleChange}
                fullWidth
              >
                {discountTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                name="delivery_fee_discount_value"
                label="Valor"
                type="number"
                value={campaign.delivery_fee_discount_value}
                onChange={handleChange}
                fullWidth
              />
            </Box>

            <Box display="flex" gap={2}>
              <TextField
                select
                name="platform_fee_discount_type"
                label="Desconto taxa plataforma"
                value={campaign.platform_fee_discount_type}
                onChange={handleChange}
                fullWidth
              >
                {discountTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                name="platform_fee_discount_value"
                label="Valor"
                type="number"
                value={campaign.platform_fee_discount_value}
                onChange={handleChange}
                fullWidth
              />
            </Box>

            <Autocomplete
              multiple
              options={tags}
              getOptionLabel={(option) => option.name}
              value={selectedTags}
              onChange={(event, value) => setSelectedTags(value)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tags obrigatórias"
                  helperText="Opcional. Se vazio, qualquer produto do desconto pode participar."
                  fullWidth
                />
              )}
            />

            <Box display="flex" justifyContent="flex-end" gap={1}>
              <Button variant="outlined" type="button" onClick={() => navigate('/control-center/campaigns')}>
                Cancelar
              </Button>
              <Button variant="contained" type="submit" disabled={saving}>
                {saving ? 'Salvando...' : 'Salvar campanha'}
              </Button>
            </Box>
          </Stack>
        </form>
      </Box>
    </FormBox>
  );
};

export default CampaignForm;
